import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Lifebuoy, ArrowCounterClockwise } from "@phosphor-icons/react";
import { kioskApi } from "@/lib/kioskApi";

const MSG = {
  sending: "Calling a team member…",
  open: "Your request has been sent to our team.",
  acknowledged: "A team member has received your request and will help you shortly.",
  failed: "We couldn't reach the team right now — please wave to a staff member at the counter.",
};

// Customer "Get help": one request per session (server dedupes repeat presses), status polled until staff complete it.
// Works without the AI service — it's a plain request to the server.
export function useKioskHelp(sessionId, onPause) {
  const [status, setStatus] = useState(null);
  const sid = useRef(sessionId);
  useEffect(() => { sid.current = sessionId; setStatus(null); }, [sessionId]);
  useEffect(() => {
    if (!sessionId || !["open", "acknowledged"].includes(status)) return undefined;
    const t = setInterval(() => kioskApi.get(`/sessions/${sessionId}/help`).then((r) => {
      if (sid.current !== sessionId) return;
      const st = r.data.help?.status;
      setStatus(st === "completed" ? null : st || null);
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [sessionId, status]);
  const request = useCallback(async () => {
    if (!sessionId) return;
    onPause?.();
    setStatus((s) => (s === "acknowledged" ? s : "sending"));
    try {
      const r = await kioskApi.post(`/sessions/${sessionId}/help`, { reason: "Customer tapped 'Get help'" }, { timeout: 10000 });
      if (sid.current === sessionId) setStatus(r.data.help?.status || "open");
    } catch { if (sid.current === sessionId) setStatus("failed"); }
  }, [sessionId, onPause]);
  return { status, message: status ? MSG[status] : "", request };
}

export function HelpButton({ help }) {
  const active = ["open", "acknowledged", "sending"].includes(help.status);
  return (
    <button className={`kiosk-pill ${active ? "bg-[#fff2cc] text-[#5c4300]" : "bg-white/10 text-white"}`} data-testid="kiosk-help" onClick={help.request}>
      <Lifebuoy size={18} /> {active ? "Help requested" : "Get help"}
    </button>
  );
}

export function HelpBanner({ help }) {
  if (!help.message) return null;
  return (
    <div className="relative z-10 mx-8 mt-3 px-5 py-3 rounded-2xl bg-[#fff2cc] text-[#5c4300] text-lg flex items-center gap-3" data-testid="kiosk-help-status" data-status={help.status}>
      <Lifebuoy size={22} weight="fill" /> {help.message}
    </div>
  );
}

export function NewOrderButton({ hasItems, onConfirm }) {
  const [ask, setAsk] = useState(false);
  return (
    <>
      <button className="kiosk-pill bg-white text-[#0f2a3d] font-semibold" data-testid="kiosk-new-order" onClick={() => (hasItems ? setAsk(true) : onConfirm())}>
        <ArrowCounterClockwise size={18} weight="bold" /> Start new order
      </button>
      {ask && createPortal(
        <div className="fixed inset-0 z-[70] bg-[#0f2a3d]/70 backdrop-blur-md flex items-center justify-center p-6" data-testid="kiosk-new-order-dialog">
          <div className="bg-white rounded-[28px] max-w-md w-full p-7 kiosk-rise">
            <div className="text-2xl font-bold font-heading text-[#0f2a3d]">Start a new order?</div>
            <div className="text-[#5c7385] mt-2">Your current unfinished order will be cleared.</div>
            <div className="grid gap-3 mt-6">
              <button className="h-14 rounded-full bg-[#e2572b] text-white font-semibold text-lg" data-testid="kiosk-new-order-yes" onClick={() => { setAsk(false); onConfirm(); }}>Yes, start new order</button>
              <button className="h-14 rounded-full bg-[#eef2f5] text-[#0f2a3d] font-semibold text-lg" data-testid="kiosk-new-order-no" onClick={() => setAsk(false)}>No, keep my order</button>
            </div>
          </div>
        </div>, document.body,
      )}
    </>
  );
}
